import React from "react";
import { throttle } from "throttle-debounce";
import PropTypes from "prop-types";
import {
  KEY_LEFT,
  KEY_RIGHT,
  KEY_UP,
  KEY_DOWN,
  KEY_PAGE_UP,
  KEY_PAGE_DOWN,
  KEY_TAB,
  KEY_HOME,
  KEY_END
} from "keycode-js";
import { clamp } from "../../../utils/number-utils";

export default class SliderControl extends React.Component {
  state = {
    containerWidth: 0
  };

  boundDragStartHandler = this.handleDragStart.bind(this);
  boundDragEndHandler = throttle(200, this.handleDragEnd.bind(this));
  boundMoveHandler = this.handleMove.bind(this);
  boundResizeHandler = throttle(200, this.handleResize.bind(this));

  componentDidMount() {
    this.handleResize();
    window.addEventListener("resize", this.boundResizeHandler);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.boundResizeHandler);
  }

  handleDragStart(e) {
    e.preventDefault();
    this.handleDragEnd();
    window.addEventListener("mouseup", this.boundDragEndHandler);
    window.addEventListener("touchend", this.boundDragEndHandler);
    window.addEventListener("mousemove", this.boundMoveHandler);
    window.addEventListener("touchmove", this.boundMoveHandler);
  }

  handleDragEnd() {
    window.removeEventListener("mouseup", this.boundDragEndHandler);
    window.removeEventListener("touchend", this.boundDragEndHandler);
    window.removeEventListener("mousemove", this.boundMoveHandler);
    window.removeEventListener("touchmove", this.boundMoveHandler);
  }

  handleMove(e) {
    let evtX = e.clientX;
    if (e.touches && e.touches.length > 0) {
      evtX = e.touches[0].clientX;
    }
    const percent = this.getPercent(
      evtX -
        this.state.containerOffset -
        this.props.trackPadding -
        this.props.thumbWidth / 2
    );
    if (percent !== this.props.percent && this.props.onChange) {
      this.props.onChange(percent);
    }
  }

  handleResize() {
    this.setState({
      containerWidth: this.props.getWidth(),
      containerOffset: this.props.getContainerOffset()
    });
  }

  getTrackWidth() {
    return this.state.containerWidth - this.props.trackPadding * 2;
  }

  getPercent(normalizedX) {
    if (normalizedX <= 0) {
      return 0;
    } else if (normalizedX >= this.getTrackWidth() - this.props.thumbWidth) {
      return 1;
    }
    return normalizedX / (this.getTrackWidth() - this.props.thumbWidth);
  }

  getStyle() {
    const left =
      this.props.trackPadding +
      (this.getTrackWidth() - this.props.thumbWidth) * this.props.percent;
    return {
      left: `${left}px`
    };
  }

  handleKey = e => {
    // Block page up/down scrolling page
    if (e.keyCode !== KEY_TAB) {
      e.preventDefault();
    }
    const smallStep = 0.01;
    const bigStep = 0.1;
    let offset = 0;

    if (e.keyCode === KEY_LEFT || e.keyCode === KEY_UP) {
      offset = -smallStep;
    } else if (e.keyCode === KEY_RIGHT || e.keyCode === KEY_DOWN) {
      offset = smallStep;
    } else if (e.keyCode === KEY_PAGE_UP) {
      offset = -bigStep;
    } else if (e.keyCode === KEY_PAGE_DOWN) {
      offset = bigStep;
    } else if (e.keyCode === KEY_HOME) {
      offset = -1;
    } else if (e.keyCode === KEY_END) {
      offset = 1;
    }

    this.step(offset);
  };

  step(offset) {
    const updatedVal = clamp(this.props.percent + offset, 0, 1);
    this.props.onChange(updatedVal);
  }

  render() {
    const containerProps = {
      role: "slider",
      tabIndex: "0",
      onKeyDown: this.handleKey,
      "aria-valuemin": "0",
      "aria-valuemax": "100",
      "aria-valuenow": Math.round(this.props.percent * 100),
      "aria-label": this.props.label
    };

    return this.props.render({
      onThumbDown: this.boundDragStartHandler,
      thumbStyle: this.getStyle(),
      width: this.state.containerWidth,
      containerProps
    });
  }
}

SliderControl.propTypes = {
  thumbWidth: PropTypes.number.isRequired,
  getWidth: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  render: PropTypes.func.isRequired,
  percent: PropTypes.number.isRequired
};
